import { ThemeService } from '@services/theme/theme.service';
import { DestroyDirective } from '@shared/components/destroy-abstract/destroy.abstract';
import { Component, Input, OnInit } from '@angular/core';
import { interval } from 'rxjs';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { LoggerService } from '@core/services/logger/logger.service';

@Component({
  selector: 'app-progress-loader',
  templateUrl: './progress-loader.component.html',
  styleUrls: ['./progress-loader.component.scss'],
  standalone: false
})
export class ProgressLoaderComponent
  extends DestroyDirective
  implements OnInit
{
  /**
   * definisce se il waiting è completato
   */
  @Input() completed: boolean;

  /**
   * durata stimata del tempo di waiting (in ms)
   */
  @Input() estimatedDuration: number;

  /**
   * codice messaggio
   */
  @Input() message: string;

  /**
   * valore della progress bar (in %)
   */
  progress = 0;

  /**
   * stato di avanzamento
   */
  status: 'end' | 'patience' | 'half' | 'begin';

  /**
   * codice colore di riempimento
   */
  progressColor: string;

  /**
   * codice colore di base
   */
  trackColor: string;

  /**
   * valore massimo (in %)
   */
  readonly max = 100;

  /**
   * valore minimo (in %)
   */
  readonly min = 0;

  /**
   * valore massimo raggiungibile finchè non diventa completed (in %)
   */
  private readonly stallMax = 99;

  /**
   * frequenza di aggiornamento (in ms)
   */
  private readonly intervalTime = 1000;

  /**
   * valore dell'incremento (in %)
   */
  private step = 0;

  /**
   * durata (in ms)
   */
  private effectiveDuration = 0;

  constructor(
    private themeService: ThemeService,
    private loggerService: LoggerService
  ) {
    super();
  }

  ngOnInit() {
    this.setColors();
    this.startCounting();
  }

  /**
   * imposta i colori leggendo il primary color
   */
  private setColors(): void {
    const rgb = this.themeService.getStyleValue('--ion-color-primary-rgb');

    if (rgb) {
      this.trackColor = `rgba(${rgb}, 0.4)`;
      this.progressColor = `rgb(${rgb})`;
    }
  }

  /**
   * inizia l'incremento
   */
  private startCounting(): void {
    this.resetCounting();
    this.calcStep();
    this.updateCounting();

    interval(this.intervalTime)
      .pipe(
        takeWhile(() => !this.completed, true),
        takeUntil(this.destroy$)
      )
      .subscribe(
        () => {
          this.updateCounting();
        },
        err => {
          this.loggerService.logError(err);
        }
      );
  }

  /**
   * azzera i valori del conteggio
   */
  private resetCounting(): void {
    this.progress = 0;
    this.effectiveDuration = 0;
    this.status = null;
  }

  /**
   * aggiorna i valori del conteggio
   */
  private updateCounting(): void {
    this.updateProgress();
    this.updateEffectiveDuration();
    this.updateStatus();
  }

  /**
   * aggiorna lo stato di avanzamento
   */
  private updateProgress(): void {
    if (this.completed) {
      this.progress = this.max;
    } else if (this.progress + this.step > this.stallMax) {
      this.progress = this.stallMax;
    } else {
      this.progress += this.step;
    }
  }

  /**
   * aggiorna la durata effettiva
   */
  private updateEffectiveDuration(): void {
    this.effectiveDuration += this.intervalTime;
  }

  /**
   * aggiorna lo status
   */
  private updateStatus(): void {
    if (this.progress >= this.max) {
      this.status = 'end';
    } else if (this.progress === this.stallMax && this.isLongDuration()) {
      this.status = 'patience';
    } else if (this.progress >= this.max / 2) {
      this.status = 'half';
    } else {
      this.status = 'begin';
    }
  }

  /**
   * calcola il valore dell'incremento
   */
  private calcStep(): void {
    this.step = Math.round(100 / (this.estimatedDuration / this.intervalTime));
  }

  /**
   * determina se il waiting è più lungo del previsto
   */
  private isLongDuration(): boolean {
    return (
      this.effectiveDuration > this.estimatedDuration + this.intervalTime * 4
    );
  }
}
